import {Trans} from '@lingui/macro';
import React, {useMemo} from 'react';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {
  getHasNewContactsManagerAccounts,
  getInitialFeatureFlags,
} from '../../../store/featureFlags/featureFlagsSelectors';
import {FeatureFlagsMap} from '../../../store/featureFlags/featureFlagsState';
import {FeatureFlagsEnum} from '../../../store/featureFlags/featureFlagsTypes';
import {useBaseEditorContext} from '../../editors/baseEditorContext';
import {useRuleEditorContext} from '../../editors/ruleEditor/ruleEditorContext';
import {
  ClientRuleAction,
  RuleAction,
  RuleActionGroup,
  ruleActionsAccountGroups,
  ruleActionsGroups,
  RuleActionTypesEnum,
  ruleExpressionsActionsGroups,
  thirdPartyGroup,
} from '../ruleActionModel';
import {RuleIntegration} from '../ruleIntegrationModel';
import {isStatementDefinition, RuleActionDefinition} from '../ruleParameterModel';
import {DropdownGroup} from './dropdowns/multipleGroupsDropdownHelpers';
import {useBundleService} from './hooks/useBundleService';
import {EditorStateActions} from './hooks/useEditorState';
import {RuleActionGroupIcon} from './ruleActionGroupIcon';
import {RuleActionGroupTitle} from './ruleActionGroupTitle';
import {RuleActionsList} from './ruleActionsList';
import {RuleEditorSection} from './ruleEditorSection';

/*
 * Constants.
 */

const featureFlagsForActions: Partial<Record<string, FeatureFlagsEnum>> = {
  [RuleActionTypesEnum.AUTOLINK]: FeatureFlagsEnum.SETTINGS_AUTOLINKERS,
  [RuleActionTypesEnum.SNOOZE]: FeatureFlagsEnum.RULE_ACTION_SNOOZE_CONVERSATION,
};

/*
 * Types.
 */

interface RuleEditorConditionsProps {
  actions: ReadonlyArray<ClientRuleAction>;
  notDisplayingErrorCards?: ReadonlyArray<string>;

  onAddAction: EditorStateActions['onAddAction'];
  onUpdateAction: EditorStateActions['onUpdateAction'];
  onDeleteAction: EditorStateActions['onDeleteAction'];
  onValidation: EditorStateActions['onValidation'];
}

/*
 * Component.
 */

export const RuleEditorActions = (props: RuleEditorConditionsProps) => {
  const {notDisplayingErrorCards, onAddAction, onUpdateAction, onDeleteAction, onValidation} = props;

  const [shouldShowMoreOptions, toggleMoreOptions] = useToggle();

  const {ruleIntegration} = useRuleEditorContext();
  const actionGroups = useRuleActionGroups(ruleIntegration);

  const actions = props.actions.map((action) => ({
    action,
    actionDefinition: ruleIntegration.findActionDefinition(action),
    onUpdateAction: (updatedAction: RuleAction) =>
      onUpdateAction(
        (a: ClientRuleAction) => a.clientId === action.clientId,
        (a: ClientRuleAction) => ({...updatedAction, clientId: a.clientId}),
      ),
    onDeleteAction: () => onDeleteAction((a) => a.clientId === action.clientId),
    onValidation,
    shouldHideError: Boolean(notDisplayingErrorCards?.includes(action.clientId)),
  }));

  return (
    <RuleEditorSection
      description={<Trans id="rules-editor-section-header-then">Then…</Trans>}
      shouldShowMoreOptions={shouldShowMoreOptions}
      toggleMoreOptions={toggleMoreOptions}
    >
      <RuleActionsList
        actions={actions}
        actionGroups={actionGroups}
        onAddAction={onAddAction}
        shouldShowMoreOptions={shouldShowMoreOptions}
      />
    </RuleEditorSection>
  );
};

/*
 * Helpers.
 */

export function useRuleActionGroups(ruleIntegration: RuleIntegration) {
  const {namespace, hasExpressions} = useBaseEditorContext();

  const {unavailableActions} = useBundleService();

  const featureFlags = useAppSelector(getInitialFeatureFlags);
  const hasNewContactsManagerAccounts = useAppSelector(getHasNewContactsManagerAccounts);

  const thirdPartyItems = ruleIntegration.getThirdPartyActions();
  const thirdPartyGroups = useMemo(
    () => (thirdPartyItems.length > 0 ? [thirdPartyGroup(thirdPartyItems)] : []),
    [thirdPartyItems],
  );

  return useMemo(() => {
    const dropdownGroups = [
      ...(hasExpressions
        ? mergeActionGroups(ruleActionsGroups, ruleExpressionsActionsGroups)
        : ruleActionsGroups),
      ...(hasNewContactsManagerAccounts ? ruleActionsAccountGroups : []),
      ...thirdPartyGroups,
    ];

    return dropdownGroups.map((group) => {
      const dropdownGroup: DropdownGroup<RuleActionDefinition> = {
        title: <RuleActionGroupTitle id={group.id} />,
        icon: <RuleActionGroupIcon id={group.id} />,
        items: group.items
          .map((item: RuleActionTypesEnum | RuleActionDefinition) =>
            isStatementDefinition(item) ? item : ruleIntegration.ruleActionIdMap[item],
          )
          .filter(isDefined)
          .filter((item) => maybeFilterRuleAction(item, featureFlags))
          .filter((item) => isNotRestrictedItem(item, namespace)),
        disabledItems: [...unavailableActions]
          .map((item) => ruleIntegration.ruleActionIdMap[item])
          .filter(isDefined),
        serializeItemForSearch: (item) => `${item.name} ${item.altName ? `${item.altName} ` : ''}${item.id}`, // Adding string id to search for better relevance
        renderItemLabel: (item) => <>{item.altName || item.name}</>,
        renderItemTooltip: (item) => item.tooltip,
        getItemKey: (item) => item.name,
      };
      return dropdownGroup;
    });
  }, [
    hasExpressions,
    hasNewContactsManagerAccounts,
    thirdPartyGroups,
    unavailableActions,
    featureFlags,
    namespace,
    ruleIntegration.ruleActionIdMap,
  ]);
}

function mergeActionGroups(
  baseGroups: ReadonlyArray<RuleActionGroup>,
  extraGroups: ReadonlyArray<RuleActionGroup>,
) {
  return baseGroups.map((group) => {
    const extraGroupItems = extraGroups.find((g) => g.id === group.id)?.items || [];
    return {
      ...group,
      items: [...group.items, ...extraGroupItems],
    };
  });
}

// @TODO: Considering merging with the same helper from ruleEditorConditions.tsx.
function maybeFilterRuleAction(item: RuleActionDefinition, featureFlags: FeatureFlagsMap) {
  const featureFlag = featureFlagsForActions[item.id];

  if (!featureFlag) {
    return true;
  }

  return featureFlags[featureFlag];
}

// @TODO: Considering merging with the same helper from ruleEditorConditions.tsx.
function isNotRestrictedItem({restricted}: RuleActionDefinition, namespace: string) {
  const namespacePrefix = namespace === 'global' ? namespace : namespace.slice(0, 3);

  if (!restricted || restricted.length === 0) {
    return true;
  }

  return restricted.includes(namespacePrefix);
}
